import axios from "axios";
import { constructAndReturnValidationErrorMessage } from "./PreviewUtils";

const APIENDPOINT = process.env.REACT_APP_HOST;

export const fetchCampaignData = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/campaigns`, {
      headers: {
        Authorization: bearerToken,
      },
    });
    switch (response.status) {
      case 200:
        const data = await response.data;
        return data;

      case 400:
        return null;

      case 404:
        return null;

      case 500:
        return null;

      default:
        return null;
    }
  } catch (error) {
    return null;
  }
};

export const fetchExistingCampaigns = async (bearerToken) => {
  let message;
  try {
    const response = await axios.get(`https://${APIENDPOINT}/campaigns`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status < 500,
    });

    switch (response.status) {
      case 200:
        const data = await response.data;
        return {
          success: true,

          data: data.records,
        };

      case 404:
        message = "No campaigns exist for this user currently";

        return {
          success: false,
          error: message,
        };

      default:
        message =
          "Something went wrong while attempting to display data on the screen.";
        return {
          success: false,
          error: message,
        };
    }
  } catch (error) {
    message =
      "Something went wrong while attempting to display data on the screen.";
    return {
      success: false,
      error: message,
    };
  }
};
export const createCampaign = async (payload, bearerToken) => {
  const response = await axios.post(
    `https://${APIENDPOINT}/campaigns`,
    payload,
    {
      headers: {
        Authorization: bearerToken,
        "Content-Type": "application/json",
      },
      validateStatus: (status) => status <= 500,
    }
  );
  return response;
};

export const uploadContactsToAPI = async (csvFile, payload, bearerToken) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) =>
    formData.append(key, value)
  );
  formData.append("file", csvFile);

  try {
    const response = await axios.post(`https://${APIENDPOINT}/bulk`, formData, {
      headers: {
        Authorization: bearerToken,
        "Content-Type": "multipart/form-data",
      },
      validateStatus: (status) => status <= 500,
    });

    if (response.status === 200) {
      return {
        success: true,
        message: "Contacts uploaded successfully",
        data: response.data,
      };
    }
    else if (response.status === 403) {
      return {
        success: false,
        message: `Quota exceeded for ${response.data.details.action}: Limit ${response.data.details.limit}, Consumed ${response.data.details.current_usage}, Available ${response.data.details.limit_available}, Requested Count ${response.data.details.incoming_request_data_length}.\nContact Support to increase your Quota.`
      }
    }
    return {
      success: false,
      message: response.data?.message || "Unexpected error occurred",
    };
  } catch (err) {
    return {
      success: false,
      message: "Upload failed. Please contact support.",
    };
  }
};

const processGroup = (groupData, groupName, formData) => {
  if (!groupData) return;

  const jsonCopy = {};
  const filesInGroup = {};

  Object.entries(groupData).forEach(([key, value]) => {
    if (value instanceof File) {
      filesInGroup[key] = value;
    } else {
      jsonCopy[key] = value;
    }
  });

  // append JSON
  formData.append(groupName, JSON.stringify(jsonCopy));

  // append files under same group namespace
  Object.entries(filesInGroup).forEach(([key, file]) => {
    formData.append(key, file);
  });
};

export const saveCustomizationToDB = async (data, bearerToken) => {
  try {
    const formData = new FormData();
    formData.append("template", data.template);
    formData.append("name", data.name);
    formData.append("owner", data.owner);
    formData.append("version", data.version);

    processGroup(data.contact, "contact", formData);
    processGroup(data.customer, "customer", formData);

    const response = await axios.post(
      `https://${APIENDPOINT}/templates`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      }
    );
    return response;
  } catch (error) {
    return error.response;
  }
};
export const getCustomizedTemplatesCreatedByUser = async (bearerToken, filter) => {
  try {
    const config = {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status < 500,
    };

    if (filter) {
      config.params = { filter };
    }

    const response = await axios.get(`https://${APIENDPOINT}/templates`, config);
    switch (response.status) {
      case 200:
        return {
          success: true,
          data: response.data?.records || [],
        };
      case 404:
        return {
          success: true,
          data: [],
        };
      case 422:
        return {
          success: false,
          error:
            response.data?.message ||
            "Invalid request to get templates.",
        };
      default:
        return {
          success: false,
          error:
            response.data?.message ||
            "Something went wrong while attempting to display data on the screen.",
        };
    }
  } catch (error) {
    return {
      success: false,
      error: "The operation you attempted has failed. Please contact support.",
    };
  }
};

export const fetchTemplatesConfig = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/template-configs`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    return response;
  } catch {
    let message = "The operation you attempted has failed. Please contact support.";
    return message;
  }
};

export const fetchTemplateConfigDetails = async (owner, name, version, bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/template-configs`, {
      params: {
        name: name,
        owner: owner,
        version: version
      },
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    return response;
  } catch (error) {
    return {
      status: 500,
      message: "The operation you attempted has failed. Please contact support.",
    };
  }
};
export const requestForPDFGeneration = async (
  campaign,
  template,
  orderType,
  bearerToken
) => {
  try {
    const response = await axios.post(
      `https://${APIENDPOINT}/requests`,
      {
        campaign: campaign,
        template: template,
        delivery_method: orderType,
      },

      {
        headers: {
          Authorization: bearerToken,
        },
        timeout: 30000,
        validateStatus: (status) => status < 500,
      }
    );
    switch (response.status) {
      case 201:
        return {
          success: true,
          message: "Your request to 'Generate Print Collaterals' is accepted.\nIt will take some time to process your request.\nYou can refresh the grid to see the status of your request.",
          data: response.data,
        };
      case 403: {
        let errorMessage;
        if (response.data.details.action) {
          errorMessage = `Quota exceeded for ${response.data.details.action}: Limit ${response.data.details.limit}, Consumed ${response.data.details.current_usage}, Available ${response.data.details.limit_available}, Requested Count ${response.data.details.incoming_request_data_length}.\nContact Support to increase your Quota.`
        } else {

          const extraCreditInfo = response.data.details.extra_credit_limit !== 0 ? `Extra credit limit ${response.data.details.extra_credit_limit}, Extra credit used ${response.data.details.extra_credit_used}, ` : "";
          errorMessage = `Insufficient credits: Credit limit ${response.data.details.credit_limit}, Credit used ${response.data.details.credit_used}, ${extraCreditInfo}Available credit ${response.data.details.credit_available}, Credit required ${response.data.details.credit_required}.\nContact Support to buy extra credits.`;
        }
        return {
          success: false,
          error: errorMessage
        }
      }
      case 422:
        return {
          success: false,
          error: response.data.message
        }
      case 409:
        return {
          success: false,
          error:
            "Request with the same values is already created. Please download the file from the grid when it is complete",
        };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

export const fetchPdfGenerationRequestsForCustomer = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/requests`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });

    const data = await response.data;

    switch (response.status) {
      case 200:
        return {
          success: true,
          data: data,
        };
      case 404:
        return {
          success: true,
          data: data,
        };
      case 500:
        return {
          success: false,
          error: "The operation you attempted has failed. Please contact support.",
        };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

export const fetchCampaigns = async (BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/campaigns`, {
    headers: {
      Authorization: BEARER,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const fetchRespondedContacts = async (campaignName, BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/records`, {
    headers: {
      Authorization: BEARER,
    },
    params: {
      campaign: campaignName,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const fetchContactVisits = async (trackable, BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/related`, {
    params: {
      path: trackable,
    },
    headers: {
      Authorization: BEARER,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const fetchUsersFromDB = async (BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/users`, {
    headers: {
      Authorization: BEARER,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const createUser = async (payload, BEARER) => {
  try {
    const action = "CREATE_USER";
    const response = await axios.post(
      `https://${APIENDPOINT}/users`,
      { ...payload, action },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: BEARER,
        },
        validateStatus: (status) => status >= 200 && status <= 500,
      }
    );
    if (response.status === 201) {
      const data = response.data;
      return {
        success: true,
        message: `An account for email ${data.details.email} has been created successfully. Associated customer ID is ${data.details.customer_id}`,
      };
    } else if (response.status === 400) {
      return {
        success: false,
        message: 'Invalid request payload. Please check the inputs provided and try again.',
      };
    } else if (response.status === 409) {
      return {
        success: false,
        message: `User with email ${payload.email} already exists. Please use a different email address.`,
      };
    } else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message,
      }
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: `The operation you attempted has failed. Please contact support.`,
      };
    }
  } catch (error) {
    return {
      success: false,
      message: `A network request exception occurred`,
    };
  }
};

export const sendEmail = async (bearerToken, email, sub) => {
  try {
    const action = "RESEND_ONBOARDING_EMAIL";
    const response = await axios.post(`https://${APIENDPOINT}/users`,
      {
        email: email,
        sub: sub,
        action: action
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      })
    const data = response.data;
    if (response.status === 200) {
      return {
        success: true,
        message: `${data.message} ${email}`,
      }
    } else if (response.status === 400) {
      return {
        success: false,
        message: data,
      }
    } else if (response.status === 404) {
      return {
        success: false,
        message: `User with email ${email} does not exist.`,
      }
    }
    else if (response.status === 409) {
      return {
        success: false,
        message: data.message,
      }
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: data.message,
      }
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: `The operation you attempted has failed. Please contact support.`,
      }
    }
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,

    }
  }
}
export const generatePreviewPdf = async (data, BEARER) => {
  try {
    const action = "PREVIEW";
    const formData = new FormData();

    formData.append("name", data.name);
    formData.append("owner", data.owner);
    formData.append("action", action);
    formData.append("version", data.version)

    processGroup(data.contact, "contact", formData);
    processGroup(data.customer, "customer", formData);

    const response = await axios.post(
      `https://${APIENDPOINT}/preview`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: BEARER,
        },
        responseType: "arraybuffer",
        validateStatus: (status) => status >= 200 && status <= 500,
      }
    );
    if (response.status === 200) {
      const data = response.data;

      return {
        success: true,
        data: data
      };
    } else if (response.status === 400) {
      const decoder = new TextDecoder("utf-8");
      const responseText = decoder.decode(response.data);
      const responseJson = JSON.parse(responseText);
      if (responseJson.errors && responseJson.errors.length > 0) {
        const errorMessage = responseJson.errors[0].qrCodeContent;
        return {
          success: false,
          message: errorMessage,
        };
      }
      return {
        success: false,
        message: responseJson.message || "The request was invalid. Please check your inputs.",
      };

    } else if (response.status === 422) {
      const responseJson = JSON.parse(new TextDecoder().decode(response.data));
      return {
        success: false,
        message: constructAndReturnValidationErrorMessage(responseJson),
      };
    }
    else if (response.status === 500) {
      return {
        success: false,
        message:
          "The operation you attempted has failed. Please contact support.",
      };
    }
  } catch (error) {
    return {
      success: false,
      message:
        "The operation you attempted has failed. Please contact support.",
    };
  }
};

export const viewPreviewPdf = async (formData, BEARER) => {
  try {
    const action = "VIEW";
    const response = await axios.post(
      `https://${APIENDPOINT}/preview`,
      { ...formData, action: action },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: BEARER,
        },
        responseType: "arraybuffer",
        validateStatus: (status) => status >= 200 && status <= 500,
      }
    );
    if (response.status === 200) {
      const data = response.data;

      return {
        success: true,
        data: data,
      };
    } else if (response.status === 400) {
      const responseJson = response.data;
      if (responseJson.errors && responseJson.errors.length > 0) {
        const errorMessage = responseJson.errors[0].qrCodeContent;
        return {
          success: false,
          message: errorMessage,
        };
      }
      return {
        success: false,
        message: responseJson.message || "The request was invalid. Please check your inputs.",
      };
    } else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || "Template not found.",
      };
    } else if (response.status === 500) {
      return {
        success: false,
        message:
          "The operation you attempted has failed. Please contact support.",
      };
    }
  } catch (error) {
    return {
      success: false,
      message:
        "The operation you attempted has failed. Please contact support.",
    };
  }
};

export const editUserPartners = async (bearerToken, sub, partners) => {
  try {
    const response = await axios.patch(`https://${APIENDPOINT}/users/${sub}`,
      {
        partners: partners,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      })
    const data = response.data;
    if (response.status === 200) {
      return {
        success: true,
        message: data.message || `User updated successfully`,
      }
    }
    else if (response.status === 400) {
      return {
        success: false,
        message: data.message || `Failed to update user`,
      }
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: data.message || `User not found`,
      }
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: data.message || `Invalid inputs`,
      }
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: data.message || `The operation you attempted has failed. Please contact support.`,
      }
    } else {
      return {
        success: false,
        message: data.message || `The operation you attempted has failed. Please contact support.`,
      }
    }
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    }
  }
}

export const fetchAdminActivityLogs = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/activity-logs`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    return response;
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    }
  }
}


export const updatePdfRequestStatus = async (bearerToken, uniqueId, customerId, newStatus) => {
  try {
    const response = await axios.patch(
      `https://${APIENDPOINT}/requests/${uniqueId}`,
      {
        request_status: newStatus,
        customer_id: customerId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      }
    );
    const data = response.data;
    if (response.status === 200) {
      return {
        success: true,
        message: data.message || `Status updated successfully`,
      };
    }
    else if (response.status === 400) {
      return {
        success: false,
        message: data.message || `Failed to update status`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: data.message || `Request not found`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: data.message || `Invalid status`,
      };
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: data.message || `Failed to update PDF request status`,
      };
    }
    else {
      return {
        success: false,
        message: data.message || `Failed to update status`,
      };
    }
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
};